/* ============================================================
 * spec-sheet.js — Arteverk PDP "Spec sheet (PDF)"
 * ------------------------------------------------------------
 * Click "Spec sheet" on a product page → generates a real, on-brand
 * one-page PDF in the browser (jsPDF, self-hosted) and OPENS IT IN A
 * NEW TAB, where the browser's PDF viewer gives native Save + Print.
 *
 * Design: swoosh logo + Bodoni (name) / Cormorant (values) / Montserrat
 * (labels) on a parchment ground with brass rules; a matted, framed rug
 * image; the content block is vertically centred so the page reads as a
 * deliberate certificate, not a top-heavy form.
 *
 * Data comes from a per-product <script type="application/json"> block
 * rendered by main-product.liquid (ALL live metafields → always current,
 * incl. Matrixify CSV updates). NO price (changes w/ sales/promos/coupons).
 * Junk metafield values (e.g. "N/A", shape leaking into Colours) are
 * cleaned/skipped so the sheet always looks intentional.
 *
 * Resilient: jsPDF + fonts + logo are fetched only on first click (then
 * cached); every image/font step degrades instead of crashing; if the rich
 * build throws it retries with built-in fonts so a PDF ALWAYS opens.
 * Event-delegated on document, so it works wherever the button sits.
 * ============================================================ */
(function () {
  var CFG = window.ArteverkSpecSheet || {};
  var jspdfReady = null;
  var assetsReady = null;

  function ensureJsPDF() {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve();
    if (jspdfReady) return jspdfReady;
    jspdfReady = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = CFG.jspdfUrl; s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
    return jspdfReady;
  }

  function abToB64(buf) {
    var bytes = new Uint8Array(buf), bin = '', chunk = 0x8000;
    for (var i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
  }

  // Load an image cross-origin (Shopify CDN sends ACAO:*) → dataURL + ratio.
  function loadImg(url, type) {
    return new Promise(function (resolve) {
      if (!url) return resolve(null);
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var c = document.createElement('canvas');
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          c.getContext('2d').drawImage(img, 0, 0);
          var mime = type === 'png' ? 'image/png' : 'image/jpeg';
          resolve({ data: c.toDataURL(mime, 0.92), ratio: img.naturalHeight / img.naturalWidth });
        } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  // Brand fonts (TTF) + swoosh logo — fetched once, cached.
  function loadAssets() {
    if (assetsReady) return assetsReady;
    var f = function (u) { return u ? fetch(u).then(function (r) { return r.arrayBuffer(); }).then(abToB64).catch(function () { return null; }) : Promise.resolve(null); };
    assetsReady = Promise.all([f(CFG.bodoniUrl), f(CFG.cormorantUrl), f(CFG.montserratUrl), loadImg(CFG.logoUrl, 'png')])
      .then(function (a) {
        var fonts = (a[0] && a[1] && a[2]) ? { bodoni: a[0], cormorant: a[1], montserrat: a[2] } : null;
        return { fonts: fonts, logo: a[3] };
      });
    return assetsReady;
  }

  // ---- value cleaning (junk metafields shouldn't reach the sheet) ----
  function naLike(s) { s = String(s || '').toLowerCase().replace(/\s+/g, ''); return s === '' || s === 'n/a' || s === 'na' || s === 'none'; }
  function cleanColours(v) {
    v = String(v || '').replace(/n\s*\/\s*a/gi, ''); // kill "N/A" as a unit before splitting on "/"
    var stop = ['rectangle', 'round', 'runner', 'square', 'oval', 'oversize', 'rug', 'area rug', 'small', 'large', 'medium'];
    return v.split('/').map(function (s) { return s.trim(); }).filter(function (s) { return s && stop.indexOf(s.toLowerCase()) < 0; }).join(' / ');
  }
  function cleanRows(specs) {
    return (specs || []).map(function (p) {
      var val = String(p[1] || '').replace(/\s*\/\s*$/, '').trim();
      if (p[0] === 'Colors') val = cleanColours(val);
      return [p[0], val];
    }).filter(function (p) { return !naLike(p[1]); });
  }

  function buildSpecSheet(jsPDF, data, fonts) {
    var doc = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
    var HAVE = false;
    if (fonts) {
      try {
        doc.addFileToVFS('AvB.ttf', fonts.bodoni); doc.addFont('AvB.ttf', 'AvB', 'normal');
        doc.addFileToVFS('AvC.ttf', fonts.cormorant); doc.addFont('AvC.ttf', 'AvC', 'normal');
        doc.addFileToVFS('AvM.ttf', fonts.montserrat); doc.addFont('AvM.ttf', 'AvM', 'normal');
        HAVE = true;
      } catch (e) { HAVE = false; }
    }
    var D = HAVE ? 'AvB' : 'times', S = HAVE ? 'AvC' : 'times', U = HAVE ? 'AvM' : 'helvetica';

    var W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), M = 52;
    var INK = [33, 29, 24], BRASS = [150, 118, 73], BRASSD = [110, 85, 47], LINE = [214, 203, 186], FAINT = [231, 222, 208], PAPER = [247, 243, 236];
    function ink(c) { doc.setTextColor(c[0], c[1], c[2]); }
    function draw(c) { doc.setDrawColor(c[0], c[1], c[2]); }

    doc.setFillColor(PAPER[0], PAPER[1], PAPER[2]).rect(0, 0, W, H, 'F');
    draw(LINE); doc.setLineWidth(0.6); doc.rect(M - 18, M - 18, W - 2 * (M - 18), H - 2 * (M - 18));

    // header
    var hy = M, logoDrawn = false;
    if (data.logo) {
      try { var lw = 146, lh = lw * data.logoRatio; doc.addImage(data.logo, 'PNG', M, hy - 2, lw, lh); hy += lh; logoDrawn = true; }
      catch (e) { logoDrawn = false; }
    }
    if (!logoDrawn) { doc.setFont(D, 'normal').setFontSize(22); ink(INK); doc.text(data.brand, M, hy + 14); hy += 26; }
    doc.setFont(U, 'normal').setFontSize(7.5); ink(BRASSD);
    // Right-align manually: jsPDF's align:'right' ignores charSpace, so spaced
    // text overflows the margin. Measure width incl. spacing and place left-anchored.
    var hl = 'SPECIFICATION SHEET', hcs = 1.3, hlw = doc.getTextWidth(hl) + (hl.length - 1) * hcs;
    doc.text(hl, W - M - hlw, M + 8, { charSpace: hcs });
    var ruleY = Math.max(hy + 12, M + 48);
    draw(BRASS); doc.setLineWidth(0.9); doc.line(M, ruleY, W - M, ruleY);

    var fRuleY = H - M - 38, bodyTop = ruleY + 30, bodyBot = fRuleY - 24;

    // layout — image column + specs column, vertically centred as one block
    var rows = cleanRows(data.specs);
    var imgW = 286, colGap = 30, leftX = M, rightX = M + imgW + colGap, colW = W - 2 * M - imgW - colGap;
    var imgH = data.imgData ? Math.min(imgW * data.imgRatio, bodyBot - bodyTop) : 0;

    // measure AT the fonts we render with (splitTextToSize uses current font)
    doc.setFont(D, 'normal').setFontSize(23);
    var nameLines = doc.splitTextToSize(data.name, colW);
    var nameH = nameLines.length * 25;
    var titleLines = [], titleH = 0;
    if (data.title) { doc.setFont(U, 'normal').setFontSize(7); titleLines = doc.splitTextToSize(String(data.title).toUpperCase(), colW); titleH = titleLines.length * 10 + 14; }
    var labelGutter = 82, valW = colW - labelGutter;
    doc.setFont(S, 'normal').setFontSize(12.5);
    var rowData = rows.map(function (p) { var vl = doc.splitTextToSize(String(p[1]), valW); return { label: p[0], vl: vl, h: Math.max(23, vl.length * 14 + 9) }; });
    var rowsH = rowData.reduce(function (a, r) { return a + r.h; }, 0);
    var specsH = nameH + 6 + titleH + rowsH;
    var blockH = Math.max(imgH, specsH);
    var top = bodyTop + Math.max(0, (bodyBot - bodyTop - blockH) / 2);

    // matted, framed rug image
    if (data.imgData) {
      try {
        var ix = leftX, iyy = top + (blockH - imgH) / 2;
        draw(FAINT); doc.setLineWidth(9); doc.rect(ix, iyy, imgW, imgH);
        doc.addImage(data.imgData, 'JPEG', ix, iyy, imgW, imgH);
        draw(BRASS); doc.setLineWidth(0.5); doc.rect(ix - 0.5, iyy - 0.5, imgW + 1, imgH + 1);
      } catch (e) { /* skip image — degrade gracefully */ }
    }

    // info column
    var yy = top;
    doc.setFont(D, 'normal').setFontSize(23); ink(INK);
    doc.text(nameLines, rightX, yy + 18); yy += nameH + 6;
    if (titleLines.length) { doc.setFont(U, 'normal').setFontSize(7); ink(BRASSD); doc.text(titleLines, rightX, yy + 7, { charSpace: 0.5, lineHeightFactor: 1.5 }); yy += titleH; }
    rowData.forEach(function (r) {
      doc.setFont(U, 'normal').setFontSize(6.6); ink(BRASS);
      doc.text(String(r.label).toUpperCase(), rightX, yy + 10, { charSpace: 0.8 });
      doc.setFont(S, 'normal').setFontSize(12.5); ink(INK);
      doc.text(r.vl, rightX + labelGutter, yy + 11);
      yy += r.h; draw(FAINT); doc.setLineWidth(0.5); doc.line(rightX, yy - 6, rightX + colW, yy - 6);
    });

    // footer — contact + URL only
    var fy = fRuleY;
    draw(BRASS); doc.setLineWidth(0.8); doc.line(M, fy, W - M, fy);
    doc.setFont(U, 'normal').setFontSize(7.5); ink(INK);
    doc.text(data.addr, W / 2, fy + 18, { align: 'center', charSpace: 0.3 });
    doc.setFont(U, 'normal').setFontSize(7); ink(BRASSD);
    doc.text(data.url, W / 2, fy + 30, { align: 'center', charSpace: 0.3 });

    doc.setProperties({ title: data.filename || 'Specification Sheet', author: data.brand, subject: 'Specification Sheet' });
    return doc;
  }

  function safeName(s) {
    return (s || 'Spec Sheet').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function escAttr(s) { return String(s || '').replace(/[<>&"]/g, ' '); }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tearsheet]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;

    var dataEl = document.getElementById(btn.getAttribute('data-ts-data'));
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }

    // Open the tab NOW, inside the user gesture, so it is not popup-blocked.
    var win = window.open('', '_blank');
    if (win) {
      win.document.write('<!doctype html><meta charset="utf-8"><title>' + escAttr(data.filename) + '</title><body style="margin:0;font-family:-apple-system,Helvetica,Arial,sans-serif;background:#F6F1E9;color:#6E552F"><p style="padding:28px;font-size:14px">Preparing your spec sheet…</p></body>');
    }

    btn.setAttribute('aria-busy', 'true');
    var label = btn.querySelector('.lbl');
    var orig = label ? label.textContent : '';
    if (label) label.textContent = 'Preparing…';

    Promise.all([ensureJsPDF(), loadAssets(), loadImg(data.img, 'jpeg')])
      .then(function (res) {
        var assets = res[1], rugIm = res[2];
        if (rugIm) { data.imgData = rugIm.data; data.imgRatio = rugIm.ratio; }
        if (assets.logo) { data.logo = assets.logo.data; data.logoRatio = assets.logo.ratio; }
        var doc;
        try {
          doc = buildSpecSheet(window.jspdf.jsPDF, data, assets.fonts);
        } catch (e1) {
          // Embedded-font path threw → retry with built-in fonts so it ALWAYS opens.
          try { console.error('[spec-sheet] full build failed, retrying basic:', e1 && (e1.stack || e1.message)); } catch (e) { }
          doc = buildSpecSheet(window.jspdf.jsPDF, data, null);
        }
        var url = doc.output('bloburl');
        if (win && !win.closed) {
          // Navigate the already-open tab straight to the PDF → the browser's
          // native viewer renders it with Save + Print. The PDF /Title names it.
          win.location.href = url;
        } else {
          doc.save(safeName(data.filename) + '.pdf');
        }
      })
      .catch(function (err) {
        var msg = (err && (err.message || String(err))) || 'unknown error';
        try { console.error('[spec-sheet] generation failed:', err && (err.stack || msg)); } catch (e) { }
        if (win) { try { win.document.open(); win.document.write('<meta charset="utf-8"><body style="font-family:-apple-system,Helvetica,Arial,sans-serif;padding:28px;color:#6E552F"><p style="color:#9a1e1e;font-size:15px">The spec sheet could not be generated.</p><p style="font-size:12px;color:#999">Detail: ' + escAttr(String(msg)) + '</p></body>'); win.document.close(); } catch (e) { } }
      })
      .then(function () {
        btn.removeAttribute('aria-busy');
        if (label) label.textContent = orig;
      });
  });
})();
